import { Component, OnInit, Input, ElementRef, OnDestroy, SimpleChanges, OnChanges } from '@angular/core';
import { trigger, style, animate, transition, state } from '@angular/animations';

export type NcAnimationType = 'fadeIn' | 'blurIn' | 'blurInUp' | 'blurInDown' | 'slideUp' | 'slideDown' | 'slideLeft' | 'slideRight' | 'scaleUp' | 'scaleDown';
export type NcByType = 'text' | 'word' | 'character' | 'line';

@Component({
  selector: 'nc-animate-text',
  templateUrl: './animate-text.component.html',
  styleUrls: ['./animate-text.component.less'],
  animations: [
    trigger('fadeIn', [
      state('hidden', style({ opacity: 0, transform: 'translateY(20px)' })),
      state('visible', style({ opacity: 1, transform: 'translateY(0px)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms')),
      transition('visible => hidden', animate('{{duration}}ms', style({ opacity: 0, transform: 'translateY(20px)' })))
    ]),
    trigger('blurIn', [
      state('hidden', style({ opacity: 0, filter: 'blur(10px)' })),
      state('visible', style({ opacity: 1, filter: 'blur(0px)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms')),
      transition('visible => hidden', animate('{{duration}}ms'))
    ]),
    trigger('blurInUp', [
      state('hidden', style({ opacity: 0, filter: 'blur(10px)', transform: 'translateY(20px)' })),
      state('visible', style({ opacity: 1, filter: 'blur(0px)', transform: 'translateY(0px)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms')),
      transition('visible => hidden', animate('{{duration}}ms'))
    ]),
    trigger('blurInDown', [
      state('hidden', style({ opacity: 0, filter: 'blur(10px)', transform: 'translateY(-20px)' })),
      state('visible', style({ opacity: 1, filter: 'blur(0px)', transform: 'translateY(0px)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms')),
      transition('visible => hidden', animate('{{duration}}ms'))
    ]),
    trigger('slideUp', [
      state('hidden', style({ opacity: 0, transform: 'translateY(20px)' })),
      state('visible', style({ opacity: 1, transform: 'translateY(0px)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms')),
      transition('visible => hidden', animate('{{duration}}ms', style({ opacity: 0, transform: 'translateY(-20px)' })))
    ]),
    trigger('slideDown', [
      state('hidden', style({ opacity: 0, transform: 'translateY(-20px)' })),
      state('visible', style({ opacity: 1, transform: 'translateY(0px)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms')),
      transition('visible => hidden', animate('{{duration}}ms', style({ opacity: 0, transform: 'translateY(20px)' })))
    ]),
    trigger('slideLeft', [
      state('hidden', style({ opacity: 0, transform: 'translateX(20px)' })),
      state('visible', style({ opacity: 1, transform: 'translateX(0px)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms')),
      transition('visible => hidden', animate('{{duration}}ms', style({ opacity: 0, transform: 'translateX(-20px)' })))
    ]),
    trigger('slideRight', [
      state('hidden', style({ opacity: 0, transform: 'translateX(-20px)' })),
      state('visible', style({ opacity: 1, transform: 'translateX(0px)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms')),
      transition('visible => hidden', animate('{{duration}}ms', style({ opacity: 0, transform: 'translateX(20px)' })))
    ]),
    trigger('scaleUp', [
      state('hidden', style({ opacity: 0, transform: 'scale(0.5)' })),
      state('visible', style({ opacity: 1, transform: 'scale(1)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms cubic-bezier(0.175, 0.885, 0.32, 1.275)')),
      transition('visible => hidden', animate('{{duration}}ms', style({ opacity: 0, transform: 'scale(0.5)' })))
    ]),
    trigger('scaleDown', [
      state('hidden', style({ opacity: 0, transform: 'scale(1.5)' })),
      state('visible', style({ opacity: 1, transform: 'scale(1)' })),
      transition('hidden => visible', animate('{{duration}}ms {{delay}}ms cubic-bezier(0.175, 0.885, 0.32, 1.275)')),
      transition('visible => hidden', animate('{{duration}}ms', style({ opacity: 0, transform: 'scale(1.5)' })))
    ])
  ]
})
export class AnimateTextComponent implements OnInit, OnDestroy, OnChanges {
  /** 展示的文本内容 */
  @Input() ncText: string = '';
  /** 延迟 */
  @Input() ncDelay: number = 0;
  /** 动画类型 */
  @Input() ncAnimation: NcAnimationType = 'fadeIn';
  /** 动画分割方式 */
  @Input() ncBy: NcByType = 'word';
  /** 每个分割块动画持续时间 */
  @Input() ncDuration: number = 300;
  /** 每个分割块之间的延迟(按text分割) */
  @Input() ncDelayMultipleByText: number = 60;
  /** 每个分割块之间的延迟(按word分割) */
  @Input() ncDelayMultipleByWord: number = 50;
  /** 每个分割块之间的延迟(按character分割) */
  @Input() ncDelayMultipleByCharacter: number = 30;
  /** 每个分割块之间的延迟(按line分割) */
  @Input() ncDelayMultipleByLine: number = 60;
  /** 是否在视图可见时才开始动画 */
  private _startOnView: boolean = false;
  @Input()
  set ncStartOnView(val: boolean | string) {
    this._startOnView = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncStartOnView(): boolean {
    return this._startOnView;
  }
  /** 是否只执行一次动画 */
  private _once: boolean = false;
  @Input()
  set ncOnce(val: boolean | string) {
    this._once = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncOnce(): boolean {
    return this._once;
  }

  segments: string[] = [];
  segmentStates: string[] = [];
  private observer: IntersectionObserver | null = null;
  private timeout: any = null;

  // 交错时间
  private staggerTimings: Record<string, number> = {};

  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void {
    this.staggerTimings = {
      text: this.ncDelayMultipleByText,
      word: this.ncDelayMultipleByWord,
      character: this.ncDelayMultipleByCharacter,
      line: this.ncDelayMultipleByLine
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ncText']) {
      this.start();
    }
  }

  ngOnDestroy(): void {
    this.clear();
  }

  private start(): void {
    this.clear();
    this.splitText();
    // 初始所有段落都隐藏
    this.segmentStates = this.segments.map(() => 'hidden');
    if (this.ncStartOnView) {
      this.setupIntersectionObserver();
    } else {
      this.timeout = setTimeout(() => {
        this.animateIn();
      }, this.ncDelay);
    }
  }

  private clear(): void {
    if (this.observer) {
      this.observer.disconnect();
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  private splitText(): void {
    switch (this.ncBy) {
      case 'word':
        this.segments = this.ncText.split(/(\s+)/);
        break;
      case 'character':
        this.segments = this.ncText.split('');
        break;
      case 'line':
        this.segments = this.ncText.split('\n');
        break;
      case 'text':
      default:
        this.segments = [this.ncText];
        break;
    }
  }

  private setupIntersectionObserver(): void {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.1
    };

    this.observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        this.timeout = setTimeout(() => {
          this.animateIn();
        }, this.ncDelay);

        if (this.ncOnce) {
          this.observer?.disconnect();
        }
      } else if (!this.ncOnce) {
        this.animateOut();
      }
    }, options);

    this.observer.observe(this.elementRef.nativeElement);
  }

  // 计算每个段落的延迟时间
  private getSegmentDelay(index: number): number {
    const staggerDelay = this.staggerTimings[this.ncBy] || 50;
    return index * staggerDelay;
  }

  // 根据索引获取段落的动画状态
  getSegmentState(index: number): string {
    return this.segmentStates[index] || 'hidden';
  }

  // 获取段落类名
  getSegmentClass(index: number): string {
    let classes = 'animate-text-segment';

    if (this.ncBy === 'line') {
      classes += ' block';
    } else {
      classes += ' inline-block whitespace-pre';
    }

    if (this.ncBy === 'character') {
      classes += ' character';
    }

    return classes;
  }

  // 获取动画参数
  getAnimationParams(index: number): any {
    return {
      duration: this.ncDuration,
      delay: this.getSegmentDelay(index)
    };
  }

  // 执行进入动画
  private animateIn(): void {
    this.segmentStates.forEach((state, index) => {
      this.segmentStates[index] = 'visible';
    })
  }

  // 执行退出动画
  private animateOut(): void {
    //若存在ncDelay，离开视图后，进入动画（animateIn）可能还未执行，则需要清除timeout
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
    //隐藏显示的文本
    this.segmentStates.forEach((state, index) => {
      this.segmentStates[index] = 'hidden';
    })
  }
}
